import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import {
  IoArrowBack,
  IoCloseOutline,
  IoContractOutline,
  IoDownloadOutline,
  IoExpandOutline,
} from 'react-icons/io5';
import { formatFileSize, getFileTypeIcon } from '../chat/helpers';
import { hasPrintLayout, kindLabel, viewerKind } from './fileKinds';
import { HtmlViewer, ImageViewer, MediaViewer, TextViewer } from './BasicViewers';
import './AtlasDocumentPanel.css';
import { ViewerBoundary } from './staleBuild';

// pdf.js and SheetJS load with their viewer, not with the chat.
const PdfViewer = lazy(() => import('./PdfViewer'));
const SheetViewer = lazy(() => import('./SheetViewer'));

// Bytes of recently opened files, so switching tabs doesn't refetch them.
const CACHE_MAX_ENTRIES = 8;
const CACHE_MAX_BYTES = 200 * 1024 * 1024;
const blobCache = new Map();

function cacheGet(key) {
  const blob = blobCache.get(key);
  if (blob) { blobCache.delete(key); blobCache.set(key, blob); }
  return blob || null;
}

function cachePut(key, blob) {
  blobCache.set(key, blob);
  let total = 0;
  blobCache.forEach((b) => { total += b.size; });
  for (const [k, b] of blobCache) {
    if (blobCache.size <= CACHE_MAX_ENTRIES && total <= CACHE_MAX_BYTES) break;
    if (k === key) continue;
    blobCache.delete(k);
    total -= b.size;
  }
}

// Above this the browser has to hold the whole file; ask before pulling it.
const CONFIRM_ABOVE_BYTES = 60 * 1024 * 1024;

function errorMessage(err) {
  if (err?.code === 'preview_too_large') return 'This file is too large to preview. Download it to open it.';
  if (err?.code === 'preview_failed') return "This document couldn't be converted for preview. Download it to open it.";
  if (err?.status === 404) return 'This file is no longer available.';
  if (err?.status === 403) return "You don't have access to this file.";
  return 'The preview could not be loaded.';
}

function ViewerBody({ file, kind, blob, onShowPrintLayout }) {
  if (kind === 'pdf') return <PdfViewer blob={blob} />;
  if (kind === 'office' || kind === 'print') return <PdfViewer blob={blob} label="Preview" />;
  if (kind === 'sheet') return <SheetViewer blob={blob} file={file} onShowPrintLayout={onShowPrintLayout} />;
  if (kind === 'image') return <ImageViewer blob={blob} file={file} />;
  if (kind === 'markdown' || kind === 'text') return <TextViewer blob={blob} file={file} kind={kind} />;
  if (kind === 'html') return <HtmlViewer blob={blob} />;
  return <MediaViewer blob={blob} kind={kind} />;
}

/**
 * The preview body for one file: fetches the bytes (or the backend's PDF
 * rendition), handles large-file confirmation, errors and retry, and picks the
 * renderer. Used by the chat's document panel and the File Browser.
 */
//   autoLoadMaxBytes:    above this, the original bytes load only on "Load preview".
//   autoConvertMaxBytes: same for Word/PowerPoint, which the server converts first.
// The print layout of a sheet is always on request, so it never asks.
export function DocumentPreview({
  file,
  loadFile,
  onDownload,
  printLayout = false,
  onShowPrintLayout = null,
  autoLoadMaxBytes = CONFIRM_ABOVE_BYTES,
  autoConvertMaxBytes = Infinity,
}) {
  const baseKind = viewerKind(file);
  const [confirmedLarge, setConfirmedLarge] = useState(false);
  const [state, setState] = useState({ status: 'idle', blob: null, error: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => { setConfirmedLarge(false); }, [file?.file_id]);

  const kind = baseKind === 'sheet' && printLayout ? 'print' : baseKind;
  const variant = kind === 'office' || kind === 'print' ? 'preview' : 'original';
  const sizeBytes = Number(file?.size_bytes);
  const autoLimit = kind === 'office' ? autoConvertMaxBytes : kind === 'print' ? Infinity : autoLoadMaxBytes;
  const needsConfirm = kind !== 'none' && Number.isFinite(sizeBytes) && sizeBytes > autoLimit && !confirmedLarge;

  useEffect(() => {
    if (!file || kind === 'none' || needsConfirm) { setState({ status: 'idle', blob: null, error: null }); return undefined; }
    const key = `${variant}:${file.file_id}`;
    const cached = cacheGet(key);
    if (cached) { setState({ status: 'ready', blob: cached, error: null }); return undefined; }
    const controller = new AbortController();
    setState({ status: 'loading', blob: null, error: null });
    loadFile({ file, variant, signal: controller.signal })
      .then((blob) => {
        cachePut(key, blob);
        setState({ status: 'ready', blob, error: null });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setState({ status: 'error', blob: null, error });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.file_id, kind, variant, needsConfirm, attempt]);

  const handleDownload = useCallback(() => {
    // Hand over bytes already in memory rather than fetching them again.
    if (file) onDownload(file, cacheGet(`original:${file.file_id}`));
  }, [file, onDownload]);

  if (!file) return null;

  if (kind === 'none') {
    return (
      <div className="atlas-doc-empty">
        <div className="atlas-doc-empty-icon">{getFileTypeIcon(file.mime_type, file.filename)}</div>
        <div className="atlas-doc-empty-title">No preview for {kindLabel(file)} files</div>
        <button type="button" className="atlas-brief-btn" onClick={handleDownload}><IoDownloadOutline size={16} /> Download</button>
      </div>
    );
  }
  if (needsConfirm) {
    return (
      <div className="atlas-doc-empty">
        <div className="atlas-doc-empty-icon">{getFileTypeIcon(file.mime_type, file.filename)}</div>
        <div className="atlas-doc-empty-title">{kindLabel(file)} · {formatFileSize(file.size_bytes)}</div>
        <div className="atlas-doc-empty-sub">
          {variant === 'preview'
            ? 'A file this size takes a while to convert for preview.'
            : 'Previewing loads the whole file into the browser.'}
        </div>
        <div className="atlas-doc-empty-actions">
          <button type="button" className="atlas-brief-btn atlas-brief-btn-secondary" onClick={() => setConfirmedLarge(true)}>Load preview</button>
          <button type="button" className="atlas-brief-btn" onClick={handleDownload}><IoDownloadOutline size={16} /> Download</button>
        </div>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="atlas-doc-empty">
        <div className="atlas-doc-empty-title">{errorMessage(state.error)}</div>
        <div className="atlas-doc-empty-actions">
          <button type="button" className="atlas-brief-btn atlas-brief-btn-secondary" onClick={() => setAttempt((n) => n + 1)}>Try again</button>
          <button type="button" className="atlas-brief-btn" onClick={handleDownload}><IoDownloadOutline size={16} /> Download</button>
        </div>
      </div>
    );
  }
  if (state.status !== 'ready') {
    return (
      <div className="atlas-doc-viewer-message">
        <span className="atlas-spinner atlas-spinner-small" aria-hidden="true" />
        {variant === 'preview'
          ? ` Preparing ${kindLabel(file)} preview… the first open can take a few seconds.`
          : ' Loading…'}
      </div>
    );
  }
  return (
    <ViewerBoundary resetKey={`${file.file_id}:${kind}`}>
      <Suspense fallback={<div className="atlas-doc-viewer-message"><span className="atlas-spinner atlas-spinner-small" aria-hidden="true" /> Loading viewer…</div>}>
        <ViewerBody
          key={`${file.file_id}:${kind}`}
          file={file}
          kind={kind}
          blob={state.blob}
          onShowPrintLayout={onShowPrintLayout}
        />
      </Suspense>
    </ViewerBoundary>
  );
}

export default function AtlasDocumentPanel({
  documents,
  activeId,
  onSelect,
  onCloseTab,
  onClose,
  isFullscreen,
  onToggleFullscreen,
  isMobile,
  loadFile,
  onDownload,
}) {
  const file = documents.find((d) => d.file_id === activeId) || documents[0] || null;
  const baseKind = viewerKind(file);
  const [printLayout, setPrintLayout] = useState(false);

  useEffect(() => { setPrintLayout(false); }, [file?.file_id]);

  const handleDownload = useCallback(() => {
    // Hand over bytes already in memory rather than fetching them again.
    if (file) onDownload(file, cacheGet(`original:${file.file_id}`));
  }, [file, onDownload]);

  if (!file) return null;

  const body = (
    <DocumentPreview
      file={file}
      loadFile={loadFile}
      onDownload={onDownload}
      printLayout={printLayout}
      onShowPrintLayout={hasPrintLayout(file) ? () => setPrintLayout(true) : null}
    />
  );

  return (
    <div className={`atlas-doc-panel${isFullscreen ? ' is-fullscreen' : ''}`}>
      <div className="atlas-brief-header atlas-doc-header">
        <div className="atlas-doc-title-wrap">
          {isMobile ? (
            <button type="button" className="atlas-brief-icon-btn" onClick={onClose} aria-label="Back to chat"><IoArrowBack size={18} /></button>
          ) : null}
          <span className="atlas-doc-title-icon">{getFileTypeIcon(file.mime_type, file.filename)}</span>
          <div className="atlas-doc-title-stack">
            <span className="atlas-doc-title" title={file.filename}>{file.filename}</span>
            <span className="atlas-doc-subtitle">
              {kindLabel(file)}{file.size_bytes ? ` · ${formatFileSize(file.size_bytes)}` : ''}
            </span>
          </div>
        </div>
        <div className="atlas-brief-header-actions">
          {baseKind === 'sheet' && hasPrintLayout(file) ? (
            <div className="atlas-doc-segmented" role="group" aria-label="Spreadsheet view">
              <button type="button" className={!printLayout ? 'is-active' : ''} onClick={() => setPrintLayout(false)}>Grid</button>
              <button type="button" className={printLayout ? 'is-active' : ''} onClick={() => setPrintLayout(true)}>Print</button>
            </div>
          ) : null}
          <button type="button" className="atlas-brief-icon-btn" onClick={handleDownload} title="Download"><IoDownloadOutline size={18} /></button>
          {onToggleFullscreen ? (
            <button type="button" className="atlas-brief-icon-btn" onClick={onToggleFullscreen} title={isFullscreen ? 'Show chat' : 'Full screen'}>
              {isFullscreen ? <IoContractOutline size={18} /> : <IoExpandOutline size={18} />}
            </button>
          ) : null}
          {!isMobile ? (
            <button type="button" className="atlas-brief-icon-btn" onClick={onClose} title="Close viewer"><IoCloseOutline size={20} /></button>
          ) : null}
        </div>
      </div>
      {documents.length > 1 ? (
        <div className="atlas-doc-tabs" role="tablist">
          {documents.map((doc) => (
            <div key={doc.file_id} className={`atlas-doc-tab${doc.file_id === file.file_id ? ' is-active' : ''}`}>
              <button type="button" role="tab" aria-selected={doc.file_id === file.file_id} className="atlas-doc-tab-label" onClick={() => onSelect(doc.file_id)} title={doc.filename}>
                {getFileTypeIcon(doc.mime_type, doc.filename)}
                <span>{doc.filename}</span>
              </button>
              <button type="button" className="atlas-doc-tab-close" onClick={() => onCloseTab(doc.file_id)} aria-label={`Close ${doc.filename}`}>
                <IoCloseOutline size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <div className="atlas-doc-body">{body}</div>
    </div>
  );
}
